import AddContactModal from 'components/modals/contact/AddContactModal';
import { ContactTypePicker } from 'components/modals/contact/ContactTypePicker';
import { useModal } from 'components/modals/useModal';
import { FORM_OVERFLOW_LARGE_MODAL_STYLE } from 'components/modals/util';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useGetContactBusinessAreaListQuery } from 'store/api/contacts';
import { ClassifierData } from 'types/classifiers';
import { ContactDto, PartyType } from 'types/contacts';
import { FeatureFlag } from 'types/features';
import { GroupType, PartyGroupForm } from 'types/groups';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

const defaultValues: PartyGroupForm = {
    groupType: GroupType.CORPORATE,
    groupName: '',
    businessAreas: [],
    members: [],
    primaryContacts: [],
    relationshipManagers: [],
    relationshipPartners: [],
    groupPartnershipType: null,
    segmentType: null,
    serviceLevelType: null,
    notes: '',
};

export type GroupFormProps = {
    type: 'add' | 'edit';
    initialData?: PartyGroupForm;
    classifiers: Record<string, ClassifierData>;
};

/** The group form's state, shared by the edit and create pages and the record page's sections. */
export const useGroupForm = ({ type, initialData, classifiers }: GroupFormProps) => {
    const { openModal, closeModal } = useModal();
    const isSegmentLocked = useIsFeatureEnabled(FeatureFlag.GROUP_SEGMENT_LOCKED);

    const methods = useForm<PartyGroupForm>({
        defaultValues: initialData ? { ...defaultValues, ...initialData } : defaultValues,
        values: initialData,
    });
    const { control, setValue, getValues, formState } = methods;

    const [members, businessAreas, groupName] = useWatch({ control, name: ['members', 'businessAreas', 'groupName'] });
    const firstMemberWithTypeCompany = members?.find((party) => party.partyType === PartyType.COMPANY);

    const sortedPersonMemberIds = useMemo(() => {
        return (
            members
                .filter((member) => member.partyType === PartyType.PERSON)
                .map((member) => member.id)
                .sort((a, b) => a - b) || []
        );
    }, [members]);
    const sortedCompanyMemberIds = useMemo(() => {
        return (
            members
                .filter((member) => member.partyType === PartyType.COMPANY)
                .map((member) => member.id)
                .sort((a, b) => a - b) || []
        );
    }, [members]);
    const sortedMemberIds = useMemo(() => {
        return [...sortedPersonMemberIds, ...sortedCompanyMemberIds].sort((a, b) => a - b) || [];
    }, [sortedPersonMemberIds, sortedCompanyMemberIds]);

    const businessAreaOptions = useMemo(
        () => getValidOptionsFromClassifier<string, string>(classifiers['BUSINESS_AREA_TYPE']) ?? [],
        [classifiers]
    );

    const {
        data: contactsBusinessAreas,
        isLoading: isLoadingContactsBusinessAreas,
        isFetching: isFetchingContactsBusinessAreas,
    } = useGetContactBusinessAreaListQuery(sortedMemberIds.join(',') ?? '', {
        skip: sortedMemberIds.length === 0,
    });

    useEffect(() => {
        const initialBusinessAreas = initialData?.businessAreas || [];

        const options =
            sortedMemberIds.length > 0
                ? businessAreaOptions.filter((businessAreaOption) =>
                      (contactsBusinessAreas ?? []).find(
                          (operatesInBusinessArea) =>
                              operatesInBusinessArea.businessAreaCode === businessAreaOption.value
                      )
                  )
                : [];

        const skip =
            (type === 'add' && formState.dirtyFields['businessAreas']) ||
            (type === 'edit' && initialBusinessAreas.length > 0 && businessAreas.length > 0) ||
            (type === 'edit' && options.length === 0) ||
            isEqual(options, businessAreas) ||
            isLoadingContactsBusinessAreas ||
            isFetchingContactsBusinessAreas;

        if (skip) {
            return;
        }

        setValue('businessAreas', options, { shouldDirty: false });
    }, [
        sortedMemberIds,
        initialData,
        formState.dirtyFields,
        businessAreaOptions,
        contactsBusinessAreas,
        members,
        businessAreas,
        isLoadingContactsBusinessAreas,
        isFetchingContactsBusinessAreas,
        setValue,
        type,
    ]);

    const onAddNewPrimaryContact = (newContact: ContactDto) => {
        const fieldData = getValues('primaryContacts');
        fieldData?.push(newContact);
        setValue('primaryContacts', fieldData);
    };

    const openAddNewPrimaryContactModal = (): void => {
        openModal(
            <AddContactModal
                onContactAdd={onAddNewPrimaryContact}
                onClose={closeModal}
                contactType={PartyType.PERSON}
                preSelectedEmployer={
                    firstMemberWithTypeCompany
                        ? {
                              id: firstMemberWithTypeCompany.id,
                              name: firstMemberWithTypeCompany.name,
                              partyType: PartyType.COMPANY,
                          }
                        : undefined
                }
                connectedContactsGroupName={t('contacts.groups.contacts-connected-to-group')}
            />,
            closeModal,
            FORM_OVERFLOW_LARGE_MODAL_STYLE
        );
    };

    const onAddNewGroupMember = (newContact: ContactDto) => {
        const fieldData = getValues('members');
        fieldData?.push(newContact);
        setValue('members', fieldData);
    };

    const openContactTypePicker = (): void => {
        openModal(
            <ContactTypePicker
                onCancel={closeModal}
                onSelect={(contactType) => {
                    closeModal();
                    openModal(
                        <AddContactModal
                            onContactAdd={(value) => onAddNewGroupMember(value)}
                            onClose={closeModal}
                            contactType={contactType}
                            preSelectedEmployer={undefined}
                            preSelectedEmployee={undefined}
                        />,
                        closeModal,
                        FORM_OVERFLOW_LARGE_MODAL_STYLE
                    );
                }}
            />,
            closeModal,
            { width: '400px' }
        );
    };

    return {
        methods,
        classifiers,
        groupName,
        isSegmentLocked,
        businessAreaOptions,
        sortedPersonMemberIds,
        sortedCompanyMemberIds,
        openAddNewPrimaryContactModal,
        openContactTypePicker,
    };
};

export type GroupFormState = ReturnType<typeof useGroupForm>;
